import { useMemo } from 'react'
import dateUtils from 'shared/src/date'
import utils from 'shared/src/utils'
import Template from 'shared/src/template'
import useMediaQuerySize from './useMediaQuerySize'

import {
  BOOKINGS_FINISHED_MESSAGE,
  BOOKINGS_FULL_MESSAGE,
} from '../../settings'

const TODAY = dateUtils.dateToString(new Date())

const useFilteredCalendarData = ({
  admin = false,
  config,
  data,
  slotFilter,
} = {}) => {

  const screenSize = useMediaQuerySize()

  // this filters the data based on the current filters
  const filteredData = useMemo(() => {

    if(!config) return []

    return data
      .map(day => {

        // if we have no slots free - this will decide on what message is shown
        // booked is only incremented for booked slots in the future
        // therefore - if booked > 0 it should say "we are fully booked"
        // if booked <= 0 and past > 0 it should say "all bookings are now closed"
        // if neither are set then we just show the normal message (i.e. there were no actual slots)
        const dayFiltersApplied = {
          booked: 0,
          past: 0,
        }

        const NOW_HOUR = new Date().getHours()

        const blocks = day.blocks
          .map(block => {
            const slots = block.slots
              .map(slot => {
                const template = Template({
                  slot,
                  config,
                })
                const isBlocked = utils.isSlotBlocked(slot)
                const isAdminOnly = utils.isSlotAdminOnly(slot)
                const canBook = template(slot.templates.canBook, 'slot.templates.canBook')
                const title = template(slot.templates.title, 'slot.templates.title')
                const subtitle = template(slot.templates.subtitle, 'slot.templates.subtitle')
                let inPast = false
                if(slot.start && slot.date == TODAY) {
                  const [ hour ] = slot.start.toString().split('.')
                  inPast = parseInt(hour) < NOW_HOUR ? true : false
                }
                return Object.assign({}, slot, {
                  _calculated: {
                    isBlocked,
                    isAdminOnly,
                    canBook,
                    title,
                    subtitle,
                    inPast,
                  }
                })
              })
              .filter(slot => {
                if(!admin && slot._calculated.isAdminOnly) {
                  return false 
                }
                else if(!admin && slot._calculated.inPast) {
                  dayFiltersApplied.past++
                  return false
                }
                else if(slotFilter == 'all') {
                  return true
                }
                else if(slotFilter == 'canBook') {
                  // we have a cannot book into the future
                  // count it so we know to display "fully booked if there are no slots left"
                  if(!slot._calculated.canBook) {
                    dayFiltersApplied.booked++
                  }
                  return slot._calculated.canBook && !slot._calculated.isBlocked ? true : false
                }
                else if(slotFilter == 'booked') {
                  if(!slot._calculated.canBook) {
                    dayFiltersApplied.booked++
                  }
                  return slot._calculated.canBook ? false : true
                }
                else if(slotFilter == 'blocked') {
                  return slot._calculated.isBlocked
                }
                else if(slotFilter == 'adminOnly') {
                  return slot._calculated.isAdminOnly
                }
                else {
                  return false
                }
              })
            return Object.assign({}, block, {
              slots,
            })
          })
          .filter(block => block.slots.length > 0)

        let statusMessage = ''

        // we only show the status messages on public calendars
        if(!admin && blocks.length <= 0) {
          if(dayFiltersApplied.booked > 0) statusMessage = BOOKINGS_FULL_MESSAGE
          else if(dayFiltersApplied.past > 0) statusMessage = BOOKINGS_FINISHED_MESSAGE
        }

        return Object.assign({}, day, {
          blocks,
          statusMessage,
        })
      })

  }, [
    admin,
    config,
    data,
    slotFilter,
  ])

  // reduce to 1 day if on small screens
  const screenSizeData = useMemo(() => {
    if(!filteredData || filteredData.length <= 0) return []
    return screenSize == 'large' ?
      filteredData :
      [filteredData[0]]
  }, [
    filteredData,
    screenSize,
  ])

  return screenSizeData
}

export default useFilteredCalendarData